import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { concat, merge, Observable, of, Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  mapTo,
  switchMap,
  tap,
} from "rxjs/operators";
import {
  DataService,
  LogicalOperator,
  SharedModule,
  SortOrder,
} from "@bb-vendure/admin-ui/core";

/**
 * @description
 * A component for selecting product variants via an autocomplete-style select input.
 *
 * @example
 * ```HTML
 * <vdr-product-variant-selector
 *   (productSelected)="selectResult($event)"></vdr-product-selector>
 * ```
 *
 * @docsCategory components
 */
@Component({
  standalone: true,
  imports: [SharedModule],
  selector: "product-selector",
  templateUrl: "./product-selector.component.html",
  styleUrls: ["./product-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ProductSelectorComponent implements OnInit {
  searchInput$ = new Subject<string>();
  searchLoading = false;
  searchResults$: Observable<any>;
  _value: any;
  @Output() productSelected = new EventEmitter<any>();

  @ViewChild("autoComplete", { static: true })
  private ngSelect: NgSelectComponent;
  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.initSearchResults();
  }

  private initSearchResults() {
    const searchItems$ = this.searchInput$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.searchLoading = true)),
      switchMap((term) => {
        if (!term) {
          return this.dataService.product
            .getProducts({
              take: 10,
              sort: {
                createdAt: SortOrder.DESC,
              },
            })
            .mapSingle((result) => result.products.items);
        }
        return this.dataService.product
          .getProducts({
            take: 10,
            filter: {
              sku: {
                contains: term,
              },
              name: {
                contains: term,
              },
            },
            filterOperator: LogicalOperator.OR,
          })
          .mapSingle((result) => result.products.items);
      }),
      tap(() => (this.searchLoading = false))
    );

    const clear$ = this.productSelected.pipe(mapTo([]));
    this.searchResults$ = concat( this.dataService.product
        .getProducts({
          take: 10,
          sort: {
            createdAt: SortOrder.DESC,
          },
        })
        .mapSingle((result) => result.products.items), merge(searchItems$, clear$));
  }

  selectResult(product?: any) {
    if (product) {
      this.productSelected.emit(product);
    }
  }

  clear(){
    this.productSelected.emit(null);
  }
}
